import dayjs from "dayjs";

import { Collapse, Typo } from "../../..";
import { BarChart, BarChartData } from "../../Common/BarChart";
import { ToggleButton, ToggleButtonItem } from "../../Common/ToggleButton";
import {
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
} from "../../types";
import { formatUSDWithoutSymbol } from "../utils/formatters";
import { BarChartTooltipMemo } from "./BarChartTooltip";
import { InstrumentChartMini } from "./InstrumentChartMini";
import { TradingByLineChart } from "./TradingByLineChart";
import { TradingChartMini } from "./TradingChartMini";

type ChartType = "usd" | "lots" | "clients";

type MobileChartV2Props = {
  lineChartData: {
    title: string;
    suffix?: string;
    total: number;
    chart: { value: number; date: string }[];
    format?: (value: number) => string;
    tooltipFormat?: (value: number) => string;
  };
  graphsInstrumentBarChartData: (BarChartData & { volumeUSD: string })[];
  graphsVolumeTradedModeOptions: ToggleButtonItem[];
  selectedGraphsTotalVolumeTradedMode: ToggleButtonItem;
  onToggleChange: (item: ToggleButtonItem) => void;
  dateRange: { from: string; to: string };
  dateTitle: string;
  translatedContent: ReturnedTranslatedContentClientsPageHook["translatedContent"];
  analytics?: ClientsPageAnalytics;
  debounceTooltipChartsTrack: () => void;
};

export const ClientTradingMobileChartV2 = ({
  lineChartData,
  graphsInstrumentBarChartData,
  graphsVolumeTradedModeOptions,
  selectedGraphsTotalVolumeTradedMode,
  onToggleChange,
  dateRange,
  dateTitle,
  translatedContent,
  analytics,
  debounceTooltipChartsTrack,
}: MobileChartV2Props) => {
  if (lineChartData.total <= 0) return null;

  return (
    <div data-testid="chart-section" className="flex w-full flex-col gap-4">
      <ChartCard
        previewChart={
          <TradingChartMini
            title={lineChartData.title}
            total={lineChartData.total}
            totalCurrencyCode={lineChartData.suffix}
            data={lineChartData.chart}
            format={lineChartData.format}
          />
        }
        detailsChart={
          <>
            <ChartHeader
              title={lineChartData.title}
              total={lineChartData.total}
              totalCurrencyCode={lineChartData.suffix}
              format={lineChartData.format}
              filterControls={
                <ToggleButton
                  items={graphsVolumeTradedModeOptions}
                  selectedValue={selectedGraphsTotalVolumeTradedMode}
                  onClick={(item) => {
                    onToggleChange(item);
                    analytics?.trackTableChartTradingEvent(item.id);
                  }}
                />
              }
            />
            <div className="my-4">
              <TradingByLineChart
                title={lineChartData.title}
                type={selectedGraphsTotalVolumeTradedMode.id as ChartType}
                data={lineChartData.chart}
                unit={lineChartData.suffix}
                format={lineChartData.format}
                tooltipFormat={lineChartData.tooltipFormat}
                startDate={dayjs(dateRange.from).format("YYYY-MM-DD")}
                endDate={dayjs(dateRange.to).format("YYYY-MM-DD")}
                onHover={debounceTooltipChartsTrack}
              />
            </div>
          </>
        }
      />

      <ChartCard
        previewChart={
          <InstrumentChartMini
            data={graphsInstrumentBarChartData}
            translatedContent={translatedContent}
          />
        }
        detailsChart={
          <>
            <ChartHeader
              title={translatedContent.graphs.tradedInstrumentsClass}
              subtitle={dateTitle}
            />
            <BarChart
              data={graphsInstrumentBarChartData}
              translations={translatedContent.graphs}
              tooltip={({ value }) => (
                <BarChartTooltipMemo
                  translations={translatedContent.graphs}
                  data={value}
                />
              )}
              onHover={debounceTooltipChartsTrack}
              isMobile={true}
            />
          </>
        }
      />
    </div>
  );
};

type ChartCardProps = {
  previewChart: React.ReactNode;
  detailsChart: React.ReactNode;
};

const ChartCard = ({ previewChart, detailsChart }: ChartCardProps) => {
  return (
    <div className="flex flex-col rounded-xl">
      <div className="flex w-full flex-col rounded-xl bg-surface-brand-subtle p-4">
        <Collapse
          isMobile={true}
          previewContent={previewChart}
          detailsContent={detailsChart}
        />
      </div>
    </div>
  );
};

type ChartHeaderProps = {
  title: string;
  subtitle?: string;
  total?: number;
  totalCurrencyCode?: string;
  format?: (value: number) => string;
  filterControls?: React.ReactNode;
};

const ChartHeader = ({
  title,
  subtitle,
  total,
  totalCurrencyCode,
  format,
  filterControls,
}: ChartHeaderProps) => {
  const displayValue =
    total !== undefined
      ? format
        ? format(total)
        : formatUSDWithoutSymbol(total)
      : undefined;

  return (
    <div className="flex flex-col-reverse gap-4">
      <div className="flex flex-col items-start gap-2">
        <Typo type="labelSM" textColor="text-text-secondary">
          {title}
        </Typo>
        {subtitle && (
          <Typo type="bodySM" textColor="text-text-tertiary">
            {subtitle}
          </Typo>
        )}
        {displayValue !== undefined && (
          <div className="flex flex-row items-baseline gap-1">
            <Typo element="h4" type="headingMD" textColor="text-text-primary">
              {displayValue}
            </Typo>
            {totalCurrencyCode && (
              <Typo element="p" type="labelXS" textColor="text-text-tertiary">
                {totalCurrencyCode}
              </Typo>
            )}
          </div>
        )}
      </div>

      {filterControls}
    </div>
  );
};
